import React, {
  useState,
  useEffect,
  RefObject,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
  useRef,
} from 'react';
import axios, { CancelTokenSource } from 'axios';
import SearchIcon from '@mui/icons-material/Search';
import { CircularProgress, FilledInput, InputAdornment } from '@mui/material';

import { useDebounce } from 'hooks/useDebounce';
import { SearchSuggestionsRef } from './SearchSuggestions';
import { CustomGeoJsonLayerData, SearchGroup, Suggestion } from 'types/types';

interface SearchInputProps {
  markersList: any[];
  searchSuggestionsRef: RefObject<SearchSuggestionsRef>;
  customGeoJsonLayers?: CustomGeoJsonLayerData[];
}

export interface SearchInputRef {
  isLoading: boolean;
  clearSearchInput: () => void;
}

const getFeatureLabel = (feature: any, layerName: string): string => {
  const props = feature.properties;
  if (!props) return `${layerName} feature`;
  for (const k of ['name', 'NAME', 'label', 'LABEL', 'title', 'TITLE', 'id', 'ID']) {
    if (props[k] != null) return `${props[k]} (${layerName})`;
  }
  const firstKey = Object.keys(props)[0];
  return firstKey ? `${props[firstKey]} (${layerName})` : `${layerName} feature`;
};

const SearchInput: ForwardRefRenderFunction<
  SearchInputRef,
  SearchInputProps
> = ({ searchSuggestionsRef, markersList, customGeoJsonLayers }, ref) => {
  const [searchInput, setSearchInput] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const cancelTokenSourceRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const searchIncrementalRef = useRef<number>(0);

  const debouncedValue = useDebounce(searchInput, 300);

  const clearSearchInput = () => {
    setSearchInput('');
  };

  const handleOpenStreetMapSearchCall = async (searchText: string) => {
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel('Operation canceled due to new request.');
    }

    cancelTokenSourceRef.current = axios.CancelToken.source();
    
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/search?polygon_geojson=1&polygon_threshold=10&format=json&q=${searchText}`,
        {
          cancelToken: cancelTokenSourceRef.current.token,
        }
      );
      return response;
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log('Request canceled', error.message, searchText);
      } else {
        console.error(error);
        return {
          data:[],
        };
      }
    }
  };

  const handleMainSearch = async (searchText: string) => {
    const currentSearchId = ++searchIncrementalRef.current; // Increment search ID
    //console.log('debound timeout debounceValue['+ debouncedValue +'], searchInput[]');

    // main logic after the timeout of the search text inut, meaninig debounced value
    const result: SearchGroup = { cells: [], sites: [], mapPlaces: [], geoJsonFeatures: [] };
    if (searchText.length < 3) {
      searchSuggestionsRef.current?.setSuggestions(null);
      return;
    }
    setIsLoading(true);
    try {
      const searchKey = debouncedValue; // No need to slice unless searchInput is modified later

      // search Map Places on API
      const mapPlacesResponse = await handleOpenStreetMapSearchCall(searchKey);
      result.mapPlaces = 
        mapPlacesResponse?.data.map(mapPlaceResponse => ({
          label: `${mapPlaceResponse.display_name || mapPlaceResponse.name}${mapPlaceResponse.addresstype ? `, ${mapPlaceResponse.addresstype}` : ''}`,
          suggestionItem: mapPlaceResponse,
          type: 'mapPlace'
        } as Suggestion));
    
      // Precompute the lowercase search input to avoid recalculating it
      const lowerSearchInput = searchKey.toLowerCase();
      markersList.forEach(siteMarkerInfo => {
        if (siteMarkerInfo.searchKey.toLowerCase().includes(lowerSearchInput)) {
          // this "location" is a match, that could be any of the "sites" sitting on same location
          // or any of it's cells
          siteMarkerInfo.sites.forEach(
            (site: any, index: number) => {
              if (site['SITE NAME'].toLowerCase().includes(lowerSearchInput)) {
                result.sites.push({
                  label: `${site['SITE NAME']} (${site['TECHNOLOGY']})`,
                  suggestionItem: siteMarkerInfo,
                  siteIndex: index,
                  type: 'site'
                });
              }
            }
          );
          // now collect the matching cells if any
          siteMarkerInfo.orientations.forEach(
            (orientation: any) => orientation.markers.forEach(
              (cellMarkerInfo: any) => {
                if (cellMarkerInfo['CELL Name'].toLowerCase().includes(lowerSearchInput)) {
                  result.cells.push({
                    label: `${cellMarkerInfo['CELL Name']} (${cellMarkerInfo['TECHNOLOGY']})`,
                    suggestionItem: cellMarkerInfo,
                    type: 'cell'
                  });
                }
              })
          );
        }
      });

      // search uploaded GeoJSON features by property values
      if (customGeoJsonLayers) {
        customGeoJsonLayers.forEach(({ id, name, features }) => {
          features.forEach((feature) => {
            if (!feature?.properties) return;
            const matches = Object.values(feature.properties).some(
              (v) => String(v).toLowerCase().includes(lowerSearchInput)
            );
            if (matches) {
              result.geoJsonFeatures.push({
                label: getFeatureLabel(feature, name),
                type: 'geoJsonFeature',
                suggestionItem: { layerId: id, layerName: name, feature },
              } as Suggestion);
            }
          });
        });
      }

      // perform this check in case that the search input has changed
      // and this iteration is no longer valid
      if (currentSearchId === searchIncrementalRef.current) {
        searchSuggestionsRef.current?.setSuggestions(result);
        console.info('Showed results for Search key['+ searchText +']');
      }
      else {
        console.info('This call was omitted. Search key['+ searchText +']');
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    handleMainSearch(debouncedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  useImperativeHandle(ref, () => ({
    isLoading,
    clearSearchInput,
  }));

  return (
    <FilledInput
      fullWidth
      value={searchInput}
      onChange={(e) => setSearchInput(e.target.value)}
      placeholder="Search"
      startAdornment={
        <InputAdornment position="start">
          <SearchIcon className="search-icon" />
        </InputAdornment>
      }
      endAdornment={
        isLoading ? (
          <InputAdornment position="end">
            <CircularProgress
              sx={{ color: 'var(--primary-accent)' }}
              size={'1.2rem'}
            />
          </InputAdornment>
        ) : (
          <React.Fragment />
        )
      }
    />
  );
};

export default forwardRef<SearchInputRef, SearchInputProps>(SearchInput);
